/**
 * ConfigSaaSPage - Global SaaS Platform Configuration
 * 
 * Manage platform-wide settings:
 * - Config matrix overview
 * - Metadata schema
 * - System-wide validation
 */

import React, { useState, useEffect } from 'react';
import {
  Settings, RefreshCw, Database, Shield, FileJson, CheckCircle,
  AlertTriangle, ChevronDown, ChevronRight, Info, Download, Upload
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '../../components/ui/collapsible';
import {
  Alert,
  AlertDescription,
  AlertTitle
} from '../../components/ui/alert';
import { api } from '../../App';
import { toast } from 'sonner';

const ConfigSaaSPage = () => {
  const [loading, setLoading] = useState(true);
  const [matrixData, setMatrixData] = useState(null);
  const [auditResults, setAuditResults] = useState(null);
  const [auditing, setAuditing] = useState(false);
  const [repairing, setRepairing] = useState(false);

  useEffect(() => {
    fetchMatrix();
  }, []);

  const fetchMatrix = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/config/system/matrix');
      // Transform matrix format for display
      const transformedData = {
        categories: {},
        version: '2.0',
        stats: {
          total_categories: 0,
          total_configs: 0,
          system_count: 0,
          default_count: 0,
          editable_count: 0
        }
      };
      
      // data.matrix contains the full CONFIG_MATRIX
      if (data.matrix) {
        for (const [category, def] of Object.entries(data.matrix)) {
          transformedData.categories[category] = {
            description: def.description,
            module: def.module,
            configs: {}
          };
          transformedData.stats.total_categories++;
          
          for (const config of def.configs || []) {
            transformedData.categories[category].configs[config.code] = {
              label: config.label,
              level: config.level,
              required: config.required,
              default_value: config.default_value,
              default_color: config.default_color,
              impact: config.impact,
              metadata_schema: config.default_value ? { fields: config.default_value } : null
            };
            transformedData.stats.total_configs++;
            
            if (config.level === 'SYSTEM') transformedData.stats.system_count++;
            else if (config.level === 'DEFAULT_TENANT') transformedData.stats.default_count++;
            else transformedData.stats.editable_count++;
          }
        }
      }
      
      setMatrixData(transformedData);
    } catch (error) {
      toast.error('Erreur lors du chargement de la matrice');
    } finally {
      setLoading(false);
    }
  };

  const runAudit = async () => {
    setAuditing(true);
    try {
      const { data } = await api.get('/config/system/audit-all');
      setAuditResults(data);
      toast.success('Audit terminé');
    } catch (error) {
      toast.error('Erreur lors de l\'audit');
    } finally {
      setAuditing(false);
    }
  };

  const runRepair = async () => {
    if (!confirm('Réparer toutes les configurations incomplètes ? Cette action va mettre à jour les métadonnées manquantes.')) {
      return;
    }
    
    setRepairing(true);
    try {
      const { data } = await api.post('/config/system/repair-all');
      setAuditResults(null);
      toast.success(`Réparation terminée : ${data.total_repairs} corrections`);
      runAudit(); // Re-run audit after repair
    } catch (error) {
      toast.error('Erreur lors de la réparation');
    } finally {
      setRepairing(false);
    }
  };

  const downloadMatrix = () => {
    const blob = new Blob([JSON.stringify(matrixData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'system-configs-matrix.json';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Matrice téléchargée');
  };

  return (
    <div className="p-6 space-y-6" data-testid="config-saas-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Configuration SaaS</h1>
          <p className="text-muted-foreground">Paramètres globaux de la plateforme</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchMatrix} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
          <Button onClick={downloadMatrix} variant="outline" size="sm">
            <Download className="w-4 h-4 mr-2" />
            Exporter
          </Button>
        </div>
      </div>

      <Tabs defaultValue="matrix" className="space-y-6">
        <TabsList>
          <TabsTrigger value="matrix">
            <FileJson className="w-4 h-4 mr-2" />
            Matrice Configs
          </TabsTrigger>
          <TabsTrigger value="audit">
            <Shield className="w-4 h-4 mr-2" />
            Audit & Réparation
          </TabsTrigger>
          <TabsTrigger value="overview">
            <Database className="w-4 h-4 mr-2" />
            Vue d'ensemble
          </TabsTrigger>
        </TabsList>

        {/* Config Matrix Tab */}
        <TabsContent value="matrix">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-violet-600" />
            </div>
          ) : !matrixData ? (
            <Alert>
              <AlertTriangle className="w-4 h-4" />
              <AlertTitle>Matrice non disponible</AlertTitle>
              <AlertDescription>La matrice de configuration n'a pas pu être chargée.</AlertDescription>
            </Alert>
          ) : (
            <div className="space-y-4">
              {Object.entries(matrixData.categories || {}).map(([category, configs]) => (
                <CategorySection key={category} category={category} configs={configs} />
              ))}
            </div>
          )}
        </TabsContent>

        {/* Audit Tab */}
        <TabsContent value="audit">
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Audit des Configurations</CardTitle>
                <CardDescription>
                  Vérifie la conformité de toutes les configurations tenant par rapport à la matrice système
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-4">
                  <Button onClick={runAudit} disabled={auditing}>
                    {auditing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Shield className="w-4 h-4 mr-2" />}
                    {auditing ? 'Audit en cours...' : 'Lancer l\'audit'}
                  </Button>
                  <Button onClick={runRepair} variant="outline" disabled={repairing || !auditResults}>
                    {repairing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Settings className="w-4 h-4 mr-2" />}
                    {repairing ? 'Réparation...' : 'Réparer tout'}
                  </Button>
                </div>
                
                {auditResults && (
                  <div className="mt-6 space-y-4">
                    {/* Summary */}
                    <div className="grid grid-cols-4 gap-4">
                      <StatCard label="Tenants audités" value={auditResults.total_tenants} />
                      <StatCard label="Avec problèmes" value={auditResults.tenants_with_issues} type={auditResults.tenants_with_issues > 0 ? 'warning' : 'success'} />
                      <StatCard label="Total problèmes" value={auditResults.total_issues} type={auditResults.total_issues > 0 ? 'warning' : 'success'} />
                      <StatCard label="Catégories" value={Object.keys(auditResults.issues_by_category || {}).length} />
                    </div>
                    
                    {/* Issues by Category */}
                    {Object.keys(auditResults.issues_by_category || {}).length > 0 && (
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-base">Problèmes par catégorie</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2">
                            {Object.entries(auditResults.issues_by_category).map(([cat, details]) => (
                              <div key={cat} className="flex justify-between items-center py-2 border-b last:border-0">
                                <div>
                                  <span className="font-medium">{cat}</span>
                                  {details.common_missing_fields && (
                                    <p className="text-xs text-muted-foreground">
                                      Champs manquants: {Object.keys(details.common_missing_fields).slice(0, 3).join(', ')}
                                      {Object.keys(details.common_missing_fields).length > 3 && '...'}
                                    </p>
                                  )}
                                </div>
                                <div className="flex gap-2 items-center">
                                  <Badge variant="outline">{details.tenants_affected || 0} tenant(s)</Badge>
                                  <Badge variant="destructive">{details.total_configs_affected || 0} configs</Badge>
                                </div>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}
                    
                    {/* Per-Tenant Results */}
                    {auditResults.tenants?.length > 0 && (
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-base">Détail par tenant</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2">
                            {auditResults.tenants.filter(t => t.issues?.length > 0).map(tenant => (
                              <Collapsible key={tenant.riad_id}>
                                <CollapsibleTrigger className="flex w-full justify-between items-center py-2 hover:bg-muted/50 px-2 rounded">
                                  <span className="font-medium">{tenant.riad_name}</span>
                                  <div className="flex items-center gap-2">
                                    <Badge variant="destructive">{tenant.issues.length} problème(s)</Badge>
                                    <ChevronDown className="w-4 h-4" />
                                  </div>
                                </CollapsibleTrigger>
                                <CollapsibleContent className="pl-4 pt-2">
                                  <ul className="space-y-1 text-sm text-muted-foreground">
                                    {tenant.issues.map((issue, i) => (
                                      <li key={i} className="flex items-start gap-2">
                                        <AlertTriangle className="w-3 h-3 mt-1 text-amber-500" />
                                        <span>{issue.category} / {issue.code}: {issue.issue}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </CollapsibleContent>
                              </Collapsible>
                            ))}
                            {auditResults.tenants.filter(t => t.issues?.length > 0).length === 0 && (
                              <div className="text-center py-4 text-green-600">
                                <CheckCircle className="w-8 h-8 mx-auto mb-2" />
                                <p>Tous les tenants sont conformes</p>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="grid grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Statistiques Configuration</CardTitle>
              </CardHeader>
              <CardContent>
                {matrixData && (
                  <div className="space-y-4">
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-muted-foreground">Catégories définies</span>
                      <span className="font-medium">{matrixData.stats?.total_categories || Object.keys(matrixData.categories || {}).length}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-muted-foreground">Configurations totales</span>
                      <span className="font-medium">{matrixData.stats?.total_configs || 0}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-muted-foreground">Configs SYSTEM (immutables)</span>
                      <Badge className="bg-red-100 text-red-700">{matrixData.stats?.system_count || 0}</Badge>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-muted-foreground">Configs DEFAULT_TENANT</span>
                      <Badge className="bg-blue-100 text-blue-700">{matrixData.stats?.default_count || 0}</Badge>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-muted-foreground">Configs TENANT_EDITABLE</span>
                      <Badge className="bg-gray-100 text-gray-600">{matrixData.stats?.editable_count || 0}</Badge>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="text-muted-foreground">Version matrice</span>
                      <span className="font-medium">{matrixData.version || '1.0'}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Documentation</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert>
                  <Info className="w-4 h-4" />
                  <AlertTitle>Matrice de Configuration</AlertTitle>
                  <AlertDescription>
                    La matrice définit toutes les configurations système et leurs métadonnées.
                    Chaque tenant hérite de cette matrice lors de sa création.
                  </AlertDescription>
                </Alert>
                <Alert>
                  <Info className="w-4 h-4" />
                  <AlertTitle>Audit & Réparation</AlertTitle>
                  <AlertDescription>
                    L'audit vérifie que tous les tenants ont les configurations requises avec les bonnes métadonnées.
                    La réparation ajoute les métadonnées manquantes automatiquement.
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Category Section Component
const CategorySection = ({ category, configs }) => {
  const [isOpen, setIsOpen] = useState(false);
  const configCount = Object.keys(configs.configs || {}).length;
  
  return (
    <Card>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger className="w-full">
          <CardHeader className="flex flex-row items-center justify-between cursor-pointer hover:bg-muted/50">
            <div className="flex items-center gap-3">
              {isOpen ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
              <div className="text-left">
                <CardTitle className="text-base">{category}</CardTitle>
                <CardDescription>{configs.description} • {configCount} configuration(s)</CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-xs">{configs.module}</Badge>
              <Badge className={configCount > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                {configCount}
              </Badge>
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        
        <CollapsibleContent>
          <CardContent className="pt-0">
            {configCount === 0 ? (
              <div className="py-4 text-center text-muted-foreground">
                <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-amber-500" />
                <p>Aucune configuration définie pour cette catégorie</p>
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-3 font-medium">Code</th>
                      <th className="text-left p-3 font-medium">Label</th>
                      <th className="text-left p-3 font-medium">Niveau</th>
                      <th className="text-left p-3 font-medium">Requis</th>
                      <th className="text-left p-3 font-medium">Impact</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(configs.configs || {}).map(([code, config], idx) => (
                      <tr key={`${category}-${code}-${idx}`} className="border-t">
                        <td className="p-3 font-mono text-xs">{code}</td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            {config.default_color && (
                              <span 
                                className="w-3 h-3 rounded-full" 
                                style={{ backgroundColor: config.default_color }}
                              />
                            )}
                            {config.label || code}
                          </div>
                        </td>
                        <td className="p-3">
                          <Badge 
                            variant={config.level === 'SYSTEM' ? 'default' : 'secondary'} 
                            className={`text-xs ${
                              config.level === 'SYSTEM' ? 'bg-red-100 text-red-700' :
                              config.level === 'DEFAULT_TENANT' ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-600'
                            }`}
                          >
                            {config.level || 'TENANT'}
                          </Badge>
                        </td>
                        <td className="p-3">
                          {config.required ? (
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="p-3 text-xs text-muted-foreground max-w-[200px] truncate">
                          {config.impact || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};

// Stat Card Component
const StatCard = ({ label, value, type = 'default' }) => {
  const colors = {
    default: 'bg-muted/50',
    success: 'bg-green-50 text-green-700',
    warning: 'bg-amber-50 text-amber-700'
  };
  
  return (
    <div className={`p-4 rounded-lg ${colors[type]}`}>
      <p className="text-xs text-muted-foreground uppercase">{label}</p>
      <p className="text-2xl font-bold">{value}</p>
    </div>
  );
};

export default ConfigSaaSPage;
